<template>
	<div class="flex xs-12">
		<!-- Left panel : Benefits tree -->
		<div v-if="!initialBenefitTypeId" class="xs-6 p4 br1 bc7">
			<div class="fs4 fw7 pb4 pl1">Select a benefit</div>
			<j-card class="bg5 xs-12">
				<j-expendable-box v-if="benefitGraph.length">
					<div class="fs3 c2 fw7 p3" slot="header">
						{{ $t("benefits") }}
					</div>
					<div slot="body" class="p2">
						<div v-for="(item, i) in benefitGraph" :key="i">
							<benefit-tree-node
								:node="item"
								class="mb2"
								@selected="selectedBenefit = $event"
							/>
						</div>
					</div>
				</j-expendable-box>
				<!-- If no data -->
				<div v-if="!isLoading && benefitGraph.length === 0" class="fs3 ta2 p2">
					{{ $t("errors.no_data") }}
				</div>
			</j-card>
		</div>

		<!-- Right Panel -->
		<div
			class="xs-6 p4"
			:class="{ 'xs-12': initialBenefitTypeId }"
			v-if="selectedBenefit"
		>
			<div class="fs4 fw7 pb4">{{ selectedBenefit.name }}</div>
			<!-- Disclaimer -->
			<j-card class="xs-12 fs3 c6 bg5 p4 mb4 lh150" v-if="showDisclaimer">
				This option will only take effects if there is a benefit option for all
				of its parents that need one.
				{{ getParentsNameAsString(selectedBenefit) }}
			</j-card>
			<!-- User input if isValueInput -->
			<div
				v-if="selectedBenefit && selectedBenefit.isValueInput"
				class="pb2 mb6 pb6"
			>
				<div class="fs3 fw7 pb2">Value</div>
				<div class="flex">
					<j-base-select
						class="xs-4"
						:options="dataTypeOptions"
						v-model="inputOptionDataType"
						size="small"
					/>
					<component
						class="xs-6 ml2"
						:is="manualComponentName"
						v-model="inputOptionValue"
						size="small"
					/>
					<j-button
						v-if="inputOptionDataType"
						class="ml2"
						type="primary"
						size="small"
						:isLoading="isLoading"
						@click="addManualOption"
						>{{ $t("save") }}</j-button
					>
				</div>
			</div>
			<!-- Options -->
			<div v-else>
				<div class="fs3 fw7 pb2 pl1">Options</div>
				<!-- Options list -->
				<j-card
					class="flex xs-between xs-12 bg5 fs3 p2 mb2 cu2 r2"
					v-for="option in computedOptions"
					:key="option.optionKey"
				>
					<div class="xs-6 flex xs-middle">
						<!-- <div class="flex xs-middle xs-12 c6 p1 fw7">{{ option.name }}</div> -->
						<div class="flex xs-middle xs-12 p1">
							{{ option.formattedValue }}
						</div>
					</div>
					<div class="xs-6 flex xs-right xs-middle">
						<j-button
							:isLoading="isLoading"
							type="primary"
							size="xsmall"
							@click="onSelectOption(option)"
							>{{ $t("save") }}</j-button
						>
					</div>
				</j-card>
				<!-- No options -->
				<div v-if="!computedOptions.length" class="xs-12 ta2">
					<j-card class="p4 bg5 fs3 c6">{{ $t("errors.no_data") }}</j-card>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JBaseSelect from "@/components/JBaseSelect"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseToggle from "@/components/JBaseToggle"
import JBaseDatePickerSimple from "@/components/JBaseDatePickerSimple"
import JButton from "@/components/JButton"
import CrmTree from "@/components/CrmTree"
import JExpendableBox from "@/components/JExpendableBox"
import BenefitTreeNode from "./BenefitTreeNode"

export default {
	components: {
		JCard,
		CrmTree,
		JExpendableBox,
		BenefitTreeNode,
		JButton,
		JBaseSelect,
		JBaseInputText,
		JBaseInputNumber,
		JBaseDatePickerSimple,
		JBaseToggle
	},

	props: {
		/*
		 * Array [benefitGraph]
		 * Represents all the benefit tree of a product
		 * Is used to display options that we can select
		 */
		value: Array,

		initialBenefitTypeId: String,

		// Initial value for isInputValue benefits
		initialInputValue: [Object, Date, Array, String, Number],
		initialInputDataType: String,

		isLoading: Boolean
	},

	data() {
		return {
			selectedBenefit: null,
			selectedOption: null,
			dataTypeOptions: [
				{ name: "Text", value: "stringValue" },
				{ name: "Date", value: "dateValue" },
				{ name: "Boolean", value: "booleanValue" },
				{ name: "Number", value: "numberValue" }
			],
			inputOptionDataType: null,
			inputOptionValue: null
		}
	},

	computed: {
		computedOptions() {
			return this?.selectedBenefit?.options || []
		},

		benefitGraph() {
			this.value.forEach(value => this.addParentTypeIdIfEditable(value))
			return this.value
		},

		showDisclaimer() {
			return (
				this.selectedBenefit?.editableParentsName &&
				this.selectedBenefit?.options?.length
			)
		},

		manualComponentName() {
			return this.inputOptionDataType === "stringValue"
				? "j-base-input-text"
				: this.inputOptionDataType === "dateValue"
				? "j-base-date-picker-simple"
				: this.inputOptionDataType === "numberValue"
				? "j-base-input-number"
				: this.inputOptionDataType === "booleanValue"
				? "j-base-toggle"
				: null
		}
	},

	methods: {
		onSelectOption(option) {
			this.$emit("benefit:saved", option)
		},

		selectInitialBenefit(benefitGraphs) {
			if (
				benefitGraphs.find(
					benefit => benefit.typeId === this.initialBenefitTypeId
				)
			) {
				this.selectedBenefit = benefitGraphs.find(
					benefit => benefit.typeId === this.initialBenefitTypeId
				)
			} else {
				benefitGraphs.forEach(benefitGraph => {
					this.selectInitialBenefit(benefitGraph.children)
				})
			}
		},

		addManualOption() {
			const newOption = {
				value: {
					[this.inputOptionDataType]: this.inputOptionValue
				},
				typeId: this.selectedBenefit.typeId,
				name: this.selectedBenefit?.name
			}
			this.$emit("benefit:saved", newOption)
		},

		// Go through all graph and add to every node the name of it's editable parents
		// Used for the disclaimer
		addParentTypeIdIfEditable(benefitGraph) {
			// If benefitGraph have isInputValue to true, or options array, append it's name to every children
			if (benefitGraph?.options?.length || benefitGraph?.isValueInput) {
				benefitGraph.children.forEach(child =>
					child.editableParentsName
						? child.editableParentsName.find(name => name === benefitGraph.name)
							? null
							: child.editableParentsName.push(benefitGraph.name)
						: (child.editableParentsName = [benefitGraph.name])
				)
			}
			// Do the same on children
			benefitGraph?.children?.forEach(child =>
				this.addParentTypeIdIfEditable(child)
			)
		},

		getParentsNameAsString(benefit) {
			return "(" + benefit?.editableParentsName.join(",") + ")"
		}
	},

	created() {
		if (this.initialBenefitTypeId) this.selectInitialBenefit(this.benefitGraph)
		if (this.initialInputValue) this.inputOptionValue = this.initialInputValue
		if (this.initialInputDataType)
			this.inputOptionDataType = this.initialInputDataType
	},

	watch: {
		inputOptionDataType: {
			handler(n, o) {
				if (o !== null && o !== undefined) {
					if (n === "stringValue") this.inputOptionValue = ""
					if (n === "dateValue") this.inputOptionValue = null
					if (n === "booleanValue") this.inputOptionValue = false
					if (n === "numberValue") this.inputOptionValue = 0
				}
			}
		}
	}
}
</script>
